import { useState, useEffect } from "react";
import { Link } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import healthWalletLogo from "@assets/generated_images/healthwallet_shield_heart_logo.png";
import {
  Menu,
  X,
  LogIn,
  UserPlus2,
  Wallet,
  HeartHandshake,
  Heart,
  Search,
  Briefcase,
  Info,
  Shield,
  Globe,
} from "lucide-react";

const navLinks = [
  { href: "/conditions", label: "Browse by Condition", icon: HeartHandshake },
  { href: "/community-funds", label: "Community Funds", icon: Heart },
  { href: "/wallet/discover", label: "Discover Wallets", icon: Search },
  { href: "/small-business/signup", label: "For Businesses", icon: Briefcase },
  { href: "/about", label: "About", icon: Info },
];

function Navbar({ hasHero = false }: { hasHero?: boolean }) {
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(!hasHero);

  useEffect(() => {
    if (!hasHero) return;
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [hasHero]);

  return (
    <nav
      className={`sticky top-0 z-[9999] w-full transition-all duration-200 ${
        scrolled
          ? "bg-background/95 backdrop-blur-md shadow-sm border-b border-border"
          : "bg-transparent"
      }`}
      data-testid="navbar-landing"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between gap-4 h-16">
          <Link href="/">
            <div className="flex items-center gap-2.5 cursor-pointer" data-testid="link-navbar-home">
              <img
                src={healthWalletLogo}
                alt="HealthWallet"
                className="h-9 w-9 object-contain"
                width="1024"
                height="1024"
              />
              <span className={`font-bold text-lg transition-colors ${scrolled ? "text-foreground" : "text-white"}`}>
                HealthWallet
              </span>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link key={link.href} href={link.href}>
                <Button
                  variant="ghost"
                  className={`text-sm font-medium ${
                    scrolled
                      ? "text-muted-foreground"
                      : "text-white/80"
                  }`}
                  data-testid={`link-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <link.icon className="w-4 h-4 mr-1.5" />
                  {link.label}
                </Button>
              </Link>
            ))}
          </div>

          {!user && (
            <div className="hidden md:flex items-center gap-2">
              <Link href="/auth">
                <Button
                  variant="ghost"
                  className={`font-medium ${
                    scrolled ? "text-foreground" : "text-white"
                  }`}
                  data-testid="button-nav-signin"
                >
                  <LogIn className="w-4 h-4 mr-1.5" />
                  Sign In
                </Button>
              </Link>
              <Link href="/auth?tab=register">
                <Button
                  className={`font-semibold ${
                    scrolled
                      ? ""
                      : "bg-white text-primary"
                  }`}
                  data-testid="button-nav-signup"
                >
                  <UserPlus2 className="w-4 h-4 mr-1.5" />
                  Sign Up Free
                </Button>
              </Link>
            </div>
          )}

          {user && (
            <div className="hidden md:flex items-center gap-2">
              <Link href="/wallet">
                <Button
                  className={`font-semibold ${
                    scrolled
                      ? ""
                      : "bg-white text-primary"
                  }`}
                  data-testid="button-nav-dashboard"
                >
                  <Wallet className="w-4 h-4 mr-1.5" />
                  My Wallet
                </Button>
              </Link>
            </div>
          )}

          <Button
            size="icon"
            variant="ghost"
            className={`md:hidden ${scrolled ? "" : "text-white"}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="button-nav-mobile-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        {mobileOpen && (
          <div className="md:hidden pb-4 space-y-1 border-t border-border/20 pt-3">
            {navLinks.map((link) => (
              <Link key={link.href} href={link.href}>
                <Button
                  variant="ghost"
                  className={`w-full justify-start text-sm font-medium ${
                    scrolled ? "text-foreground" : "text-white/90"
                  }`}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`link-mobile-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <link.icon className="w-4 h-4 mr-2" />
                  {link.label}
                </Button>
              </Link>
            ))}
            <div className="border-t border-border/20 pt-2 mt-2 space-y-1">
              {!user ? (
                <>
                  <Link href="/auth">
                    <Button
                      variant="ghost"
                      className={`w-full justify-start font-medium ${
                        scrolled ? "text-foreground" : "text-white"
                      }`}
                      onClick={() => setMobileOpen(false)}
                      data-testid="button-mobile-signin"
                    >
                      <LogIn className="w-4 h-4 mr-2" />
                      Sign In
                    </Button>
                  </Link>
                  <Link href="/auth?tab=register">
                    <Button
                      className={`w-full justify-start font-semibold ${
                        scrolled ? "" : "bg-white text-primary"
                      }`}
                      onClick={() => setMobileOpen(false)}
                      data-testid="button-mobile-signup"
                    >
                      <UserPlus2 className="w-4 h-4 mr-2" />
                      Sign Up Free
                    </Button>
                  </Link>
                </>
              ) : (
                <Link href="/wallet">
                  <Button
                    className={`w-full justify-start font-semibold ${
                      scrolled ? "" : "bg-white text-primary"
                    }`}
                    onClick={() => setMobileOpen(false)}
                    data-testid="button-mobile-dashboard"
                  >
                    <Wallet className="w-4 h-4 mr-2" />
                    My Wallet
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

function Footer() {
  const { user } = useAuth();

  return (
    <footer className="py-10 sm:py-16 px-4 sm:px-6 bg-muted/50 border-t" data-testid="footer-landing">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 mb-10">
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <img
                src={healthWalletLogo}
                alt="HealthWallet Logo"
                className="h-10 w-10 object-contain"
                width="1024"
                height="1024"
                data-testid="img-footer-logo"
              />
              <span className="font-bold text-lg">HealthWallet</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              Pre-funded care. Community support. Peace of mind. Serving the Minneapolis-St. Paul metro area.
            </p>
            <div className="flex items-center gap-4 text-muted-foreground">
              <div className="flex items-center gap-1.5 text-xs">
                <Shield className="w-3.5 h-3.5" />
                <span>Bank-Level Security</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs">
                <Globe className="w-3.5 h-3.5" />
                <span>4 Languages</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-sm mb-4">Explore</h4>
            <ul className="space-y-2.5 text-sm text-muted-foreground">
              {navLinks.filter(link => ["/conditions", "/community-funds", "/wallet/discover"].includes(link.href)).map(link => {
                const testId = link.href === "/conditions" ? "link-footer-conditions" : link.href === "/community-funds" ? "link-footer-campaigns" : "link-footer-discover";
                return (
                  <li key={link.href}>
                    <Link href={link.href} className="transition-colors" data-testid={testId}>
                      {link.label}
                    </Link>
                  </li>
                );
              })}
              <li>
                <a href="#how-it-works" className="transition-colors" data-testid="link-footer-how-it-works">
                  How It Works
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-sm mb-4">Get Started</h4>
            <ul className="space-y-2.5 text-sm text-muted-foreground">
              {!user ? (
                <>
                  <li>
                    <Link href="/auth" className="transition-colors" data-testid="link-footer-signin">
                      Sign In
                    </Link>
                  </li>
                  <li>
                    <Link href="/auth?tab=register" className="transition-colors" data-testid="link-footer-signup">
                      Create an Account
                    </Link>
                  </li>
                </>
              ) : (
                <li>
                  <Link href="/wallet" className="transition-colors" data-testid="link-footer-wallet">
                    My Wallet
                  </Link>
                </li>
              )}
              <li>
                <Link href="/auth?tab=register" className="transition-colors" data-testid="link-footer-for-hospitals">
                  For Hospitals
                </Link>
              </li>
              <li>
                <Link href="/auth?tab=register" className="transition-colors" data-testid="link-footer-for-employers">
                  For Employers
                </Link>
              </li>
              <li>
                <Link href="/about" className="transition-colors" data-testid="link-footer-about">
                  About Us
                </Link>
              </li>
              <li>
                <Link href="/pitch" className="transition-colors" data-testid="link-footer-pitch">
                  Investor Pitch
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-sm mb-4">Legal</h4>
            <ul className="space-y-2.5 text-sm text-muted-foreground">
              <li>
                <Link href="/privacy" className="transition-colors" data-testid="link-footer-privacy">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link href="/terms" className="transition-colors" data-testid="link-footer-terms">
                  Terms of Service
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-border pt-6 space-y-4">
          <p className="text-xs text-muted-foreground text-center" data-testid="text-footer-disclaimer">
            HealthWallet is a healthcare funding technology platform. HealthWallet is not a bank, insurance company, or healthcare provider. All funds are processed and held by Stripe, Inc. Payment schedules are facilitated by participating healthcare providers. Pre-care subscriptions are voluntary savings mechanisms, not insurance products.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-xs text-muted-foreground">
              &copy; {new Date().getFullYear()} HealthWallet. All rights reserved.
            </div>
            <div className="text-xs text-muted-foreground">
              Available in English, Spanish, Somali, and Hmong
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export function MarketingLayout({ children, hasHero = false }: { children: React.ReactNode; hasHero?: boolean }) {
  return (
    <>
      <Navbar hasHero={hasHero} />
      {children}
      <Footer />
    </>
  );
}
